<script lang="ts" setup>
import preventPageScrolling from '@/helpers/preventPageScrolling';

import BaseButton from '@/components/BaseButton.vue';

const props = defineProps<{
  modelValue: boolean;
  title?: string;
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void;
}>();

const closeModal = function closeModal() {
  emit('update:modelValue', false);
};

const { allowScroll, preventScroll } = preventPageScrolling();

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) {
      preventScroll();
    } else {
      allowScroll();
    }
  },
);
</script>

<template>
  <div
    class="modal-wrapper"
    :class="{
      'modal-wrapper--open': props.modelValue,
    }"
  >
    <div
      class="modal-wrapper__backdrop"
      @click.stop="closeModal"
    ></div>

    <div class="modal-wrapper__wrap">
      <div class="modal-wrapper__header">
        <slot name="header">
          <p class="modal-wrapper__title text-body-s">
            {{ props.title ?? '' }}
          </p>
        </slot>

        <base-button
          class="modal-wrapper__btn"
          @click.stop="closeModal"
        >
          <svg
            aria-hidden="true"
            class="modal-wrapper__icon"
            width="16"
            height="16"
          >
            <use
              xlink:href="@/assets/images/svg/sprite-base.svg#x-close-cross-16"
            ></use>
          </svg>
        </base-button>
      </div>

      <div class="modal-wrapper__content">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.modal-wrapper {
  $this: 'modal-wrapper';

  position: fixed;
  z-index: 5998;
  border-radius: 0 !important;
  max-width: 100vw;
  max-height: 100vh;
  pointer-events: none !important;
  inset: 0;

  &--open {
    .#{$this}__backdrop {
      opacity: 1;
      pointer-events: all;
    }

    .#{$this}__wrap {
      transform: translateY(0);
      pointer-events: all;
    }
  }

  &__backdrop {
    position: fixed;
    z-index: 5999;
    pointer-events: none;
    outline: 0;
    background: rgb(0 0 0 / 40%);
    inset: 0;
    transition: opacity $anim-primary;
    opacity: 0;
  }

  &__wrap {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-top-right-radius: $brd-16;
    border-top-left-radius: $brd-16;
    pointer-events: none;
    position: fixed;
    z-index: 6000;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: $color-white;
    max-height: calc(100vh - 96px);
    transition: transform $anim-primary;
    transform: translateY(100%);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
  }

  &__title {
    font-weight: 500;
  }

  &__btn {
    padding: 0 8px;
    margin-right: -8px;
    margin-left: 16px;
  }

  &__content {
    overflow: auto;
    padding-inline: 16px;
  }
}
</style>
